import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Play, Image as ImageIcon, Video, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import "./creation.scss";
import { PROJECTS } from "../../data/Design";

const FILTER_OPTIONS = [
  { value: "all", label: "All", Icon: Filter },
  { value: "image", label: "Image", Icon: ImageIcon },
  { value: "video", label: "Video", Icon: Video },
];

const Creation = () => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const closeButtonRef = useRef(null);
  const returnFocusRef = useRef(null);
  const wasLightboxOpenRef = useRef(false);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return PROJECTS.filter((project) => {
      const matchesType = filter === "all" || project.type === filter;
      const searchableText = `${project.title} ${project.category} ${project.description || ""}`.toLowerCase();
      return matchesType && (!normalizedQuery || searchableText.includes(normalizedQuery));
    });
  }, [filter, query]);

  const selectedIndex = filteredProjects.findIndex((project) => project.id === selectedItem?.id);

  const closeLightbox = useCallback(() => {
    setSelectedItem(null);
    requestAnimationFrame(() => returnFocusRef.current?.focus());
  }, []);

  const showRelativeProject = useCallback((offset) => {
    if (filteredProjects.length < 2 || selectedIndex < 0) return;
    const nextIndex = (selectedIndex + offset + filteredProjects.length) % filteredProjects.length;
    setSelectedItem(filteredProjects[nextIndex]);
  }, [filteredProjects, selectedIndex]);

  useEffect(() => {
    if (selectedItem && !wasLightboxOpenRef.current) closeButtonRef.current?.focus();
    wasLightboxOpenRef.current = Boolean(selectedItem);
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedItem) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowRight") showRelativeProject(1);
      if (event.key === "ArrowLeft") showRelativeProject(-1);
      if (event.key === "Tab") {
        const focusableElements = Array.from(
          document.querySelectorAll(
            ".lightbox-content button:not(:disabled), .lightbox-content iframe, .lightbox-content a[href], .lightbox-content [tabindex]:not([tabindex='-1'])",
          ),
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItem, selectedIndex, filteredProjects, closeLightbox, showRelativeProject]);

  return (
    <div className="gallery-root">
      <div className="gallery-container">
        <header className="gallery-header">
          <h1 className="gallery-title">WHERE IDEAS BECOME VISUALS</h1>
          <p className="gallery-subtitle">
            A curated showcase of my creative development and design work.
            Filter by media type or search by title and category.
          </p>
        </header>

        <label className="gallery-search-label" htmlFor="project-search">Search projects</label>
        <input
          id="project-search"
          className="gallery-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Try “branding” or “wedding”"
        />

        <nav className="filter-nav" aria-label="Filter projects by media type">
          {FILTER_OPTIONS.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`filter-btn${filter === value ? " filter-btn--active" : ""}`}
              aria-pressed={filter === value}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        <p className="gallery-result-count" aria-live="polite">
          {filteredProjects.length} {filteredProjects.length === 1 ? "project" : "projects"}
        </p>

        <div className="gallery-grid">
          {filteredProjects.map((project) => (
            <button
              key={project.id}
              type="button"
              className="gallery-card"
              onClick={(event) => {
                returnFocusRef.current = event.currentTarget;
                setSelectedItem(project);
              }}
              aria-label={`View ${project.title}, ${project.type} project`}
            >
              <img
                src={project.thumbnail}
                alt=""
                loading="lazy"
                decoding="async"
                className={`card-thumb${project.type === "video" ? " card-thumb--video" : ""}`}
              />

              {project.type === "video" && (
                <span className="play-overlay" aria-hidden="true">
                  <span className="play-btn"><Play size={28} className="play-icon" /></span>
                </span>
              )}

              <span className="card-info">
                <span className="card-category">{project.category}</span>
                <span className="card-title">{project.title}</span>
              </span>
            </button>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="empty-state"><p>No projects match your search.</p></div>
        )}
      </div>

      {selectedItem && (
        <div className="lightbox" onMouseDown={(event) => event.target === event.currentTarget && closeLightbox()}>
          <section
            className="lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lightbox-title"
          >
            <button ref={closeButtonRef} type="button" className="lightbox-close" onClick={closeLightbox} aria-label="Close project viewer">
              <X size={28} />
            </button>

            {selectedItem.type === "image" ? (
              <img
                src={selectedItem.fullRes || selectedItem.thumbnail}
                alt={selectedItem.title}
                className="lightbox-image"
              />
            ) : (
              <div className="lightbox-video-wrapper">
                <iframe
                  className="lightbox-iframe"
                  src={`https://www.youtube-nocookie.com/embed/${selectedItem.videoId}?autoplay=1&rel=0`}
                  title={selectedItem.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            <div className="lightbox-meta">
              <p className="lightbox-category">{selectedItem.category} · {selectedItem.type}</p>
              <h2 id="lightbox-title" className="lightbox-title">{selectedItem.title}</h2>
              {selectedItem.description && <p className="lightbox-description">{selectedItem.description}</p>}
              {(selectedItem.role || selectedItem.tools?.length > 0) && (
                <dl className="lightbox-details">
                  {selectedItem.role && <><dt>Role</dt><dd>{selectedItem.role}</dd></>}
                  {selectedItem.tools?.length > 0 && <><dt>Tools</dt><dd>{selectedItem.tools.join(", ")}</dd></>}
                </dl>
              )}
              <div className="lightbox-navigation">
                <button type="button" onClick={() => showRelativeProject(-1)} disabled={filteredProjects.length < 2} aria-label="Previous project">
                  <ChevronLeft size={20} /> Previous
                </button>
                <span>{selectedIndex + 1} of {filteredProjects.length}</span>
                <button type="button" onClick={() => showRelativeProject(1)} disabled={filteredProjects.length < 2} aria-label="Next project">
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Creation;
